import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Action } from '@ngrx/store'
import * as ItemActions from 'libs/gb-front/item/data-access/src/lib/+state/items.actions'
import * as CategoriesActions from 'libs/gb-front/category/data-access/src/lib/+state/categories.actions'
import { ItemFacade } from 'libs/gb-front/item/data-access/src/lib/+state/items.facade'
import { SlimItemEntity } from 'libs/gb-front/item/data-access/src/lib/+state/items.models'
import { FilterSlugEnum } from 'libs/gb-front/shared/ui/src/lib/gb-item-filters/gb-item-filters.models'
import { GbItemsObserverService } from 'libs/shared/ui/src/lib/gb-items-observer/gb-items-observer.service'
import { ObjectUtils } from 'libs/shared/utils/helpers/src/lib/object-utils'
import { RouterUtils } from 'libs/shared/utils/helpers/src/lib/router-utils'
import { LoaderFacade } from 'libs/shared/utils/loader/src'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { map, skip, withLatestFrom } from 'rxjs/operators'
import { CategoriesFacade } from 'libs/gb-front/category/data-access/src/lib/+state/categories.facade'

export type CategoryParam = { [FilterSlugEnum.Category]: string }

@UntilDestroy()
@Component({
  selector: 'gb-category-view',
  templateUrl: './gb-category-view.component.html',
  styleUrls: ['./gb-category-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbCategoryViewComponent implements OnInit, AfterViewInit {
  filteredItems$ = this.itemFacade.filteredItems$
  selectedCategory$ = this.categoriesFacade.selectedCategory$
  staleFilteredPages$ = this.itemFacade.staleFilteredPages$
  isLoading$ = this.loaderFacade.getIsLoading$(LoaderActionTypeEnum.LoadItems)

  categoryId = ''
  ITEM_LIMIT = 10
  fromUuid?: string | null

  categoryId$ = this.activatedRoute.params.pipe(map((params) => params['uuid']))

  get categoryParam(): CategoryParam {
    return {
      [FilterSlugEnum.Category]: this.categoryId,
    }
  }

  constructor(
    private categoriesFacade: CategoriesFacade,
    private itemFacade: ItemFacade,
    private activatedRoute: ActivatedRoute,
    private loaderFacade: LoaderFacade,
    private itemsObserverService: GbItemsObserverService,
    private router: Router,
  ) {
    this.fromUuid = RouterUtils.getPreviousNavigationParam(this.router, 'from')
  }

  ngOnInit(): void {
    this.categoryId$
      .pipe(withLatestFrom(this.itemFacade.currentFilters$), untilDestroyed(this))
      .subscribe(([uuid, currentFilters]) => {
        this.categoryId = uuid

        if (!ObjectUtils.isEqual(this.categoryParam, currentFilters)) {
          // scroll to top on category change
          document.querySelector('.mat-sidenav-content')?.scrollTo(0, 0)
          this.dispatchCategoryChangedActions()
        } else {
          this.itemFacade.dispatch(ItemActions.resyncFilteredItemsFirstPage())
        }
      })

    this.itemFacade.order$
      .pipe(
        skip(1), // skip current order, emit on order change
        withLatestFrom(this.itemsObserverService.getPageInView()),
        untilDestroyed(this),
      )
      .subscribe(([order, pageInView]) => {
        // check if not on first page
        if (pageInView !== 0)
          // scroll to top of category on order change
          this.scrollTo('category-view')
      })
  }

  ngAfterViewInit(): void {
    if (this.fromUuid) {
      // scroll to selected item on returning to list
      this.scrollTo(this.fromUuid)
    }
  }

  scrollTo(id: string): void {
    document.getElementById(id)?.scrollIntoView()
  }

  dispatchCategoryChangedActions(): void {
    const actions: Action[] = [
      ItemActions.clearFilteredItems(),
      ItemActions.updateFilters({ filters: this.categoryParam }),
      CategoriesActions.updateSelectedCategoryUuid({ uuid: this.categoryId }),
      ItemActions.loadFilteredItems({ filters: this.categoryParam }),
    ]
    actions.forEach((action) => this.itemFacade.dispatch(action))
  }

  getItemsText(itemCount: number): string {
    return itemCount > 1 ? 'CATEGORIES.ITEMS' : 'CATEGORIES.ITEM'
  }

  setItemsToObserve(items: ElementRef[]): void {
    this.itemsObserverService.setItemsToObserve(items)
  }

  resyncItems(offset: number, onFocus = false): void {
    this.itemFacade.dispatch(ItemActions.resyncFilteredItems(offset, this.categoryParam, onFocus))
  }

  /**
   * Load next page of items when end reached and more items to load
   * @param count
   * @param items
   */
  onEndReached(items: SlimItemEntity[] | null, count?: number | null): void {
    if (this.isMoreItemsToLoad(items, count)) {
      this.itemFacade.dispatch(
        ItemActions.loadFilteredItems({
          filters: this.categoryParam,
        }),
      )
    }
  }

  /**
   * Checks if all items for category have been loaded in store
   * @param count
   * @param items
   */
  isMoreItemsToLoad(items: SlimItemEntity[] | null, count?: number | null): boolean {
    if (count === null || count === undefined || items === null) return false
    return count > items.length
  }

  /**
   * window focus event should resync all the necessary data from the API
   * as pusher event can be missed when the window has no focus, so when the user refocus
   * set all loaded category items as stale and call function to resync page currently in view
   * @param offset
   */
  onFocus(offset: number): void {
    this.itemFacade.dispatch(ItemActions.setFilteredItemsStale())
    this.resyncItems(offset, true)
  }

  /**
   * Resync stale items on scroll
   * @param offset
   */
  onScroll(offset: number): void {
    this.resyncItems(offset)
  }

  backToCategories(): void {
    this.router.navigate(['../'], {
      relativeTo: this.activatedRoute,
    })
  }
}
